<template>
  <div>
    <base-header
      class="pb-9 pt-4"
      :type="$route.meta.headerClass ? $route.meta.headerClass : 'success'"
    >
    </base-header>
    <div class="container-fluid mt--9">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
              <span class="h3 mb-0">Companies</span>
              <div>
                <base-button
                  type="secondary"
                  size="sm"
                  class="mr-2"
                  :disabled="syncing"
                  @click="syncFromConfig"
                >
                  <i class="fa fa-sync mr-1" :class="{ 'fa-spin': syncing }"></i>
                  {{ syncing ? 'Syncing…' : 'Sync from users' }}
                </base-button>
                <base-button type="primary" size="sm" @click="openAdd">
                  <i class="fa fa-plus mr-1"></i>Add company
                </base-button>
              </div>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">
                Per-company download meter and max clip length, tracked separately for domestic and
                international matches. Download limits apply to the clips browser only. Leave a download
                limit blank for unlimited. Use “Sync from users” to create company records for every
                company name already assigned to users.
              </p>
              <p v-if="!companies.length && !loading" class="text-muted mb-3">
                No company records yet. Click “Sync from users” to import existing company names, or “Add company”.
              </p>
              <el-table
                class="table-card"
                :data="companies"
                row-key="id"
                header-row-class-name="thead-light"
                :default-sort="{ prop: 'name', order: 'ascending' }"
              >
                <el-table-column label="Name" prop="name" min-width="160" sortable fixed />
                <el-table-column label="Domestic" align="center">
                  <el-table-column label="Limit" min-width="90" align="right">
                    <template v-slot="{ row }">{{ formatLimit(getLimit(row, 'domestic')) }}</template>
                  </el-table-column>
                  <el-table-column label="Used" min-width="80" align="right">
                    <template v-slot="{ row }">{{ formatMinutes(getUsed(row, 'domestic')) }}</template>
                  </el-table-column>
                </el-table-column>
                <el-table-column label="International" align="center">
                  <el-table-column label="Limit" min-width="90" align="right">
                    <template v-slot="{ row }">{{ formatLimit(getLimit(row, 'international')) }}</template>
                  </el-table-column>
                  <el-table-column label="Used" min-width="80" align="right">
                    <template v-slot="{ row }">{{ formatMinutes(getUsed(row, 'international')) }}</template>
                  </el-table-column>
                </el-table-column>
                <el-table-column label="" min-width="290" align="right" fixed="right">
                  <template v-slot="{ row }">
                    <base-button size="sm" type="secondary" class="mr-1" @click="openBreakdown(row)">
                      Usage
                    </base-button>
                    <base-button size="sm" type="secondary" class="mr-1" @click="openEdit(row)">
                      Edit
                    </base-button>
                    <el-dropdown class="mr-1" trigger="click" @command="(cmd) => resetUsage(row, cmd)">
                      <base-button size="sm" type="warning">
                        Reset <i class="el-icon-arrow-down el-icon--right"></i>
                      </base-button>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="domestic">Reset domestic usage</el-dropdown-item>
                        <el-dropdown-item command="international">Reset international usage</el-dropdown-item>
                        <el-dropdown-item command="all">Reset both</el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                    <base-button size="sm" type="danger" @click="confirmDelete(row)">
                      Delete
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal :show.sync="showFormModal" size="lg" modal-classes="modal-scrollable">
      <div class="p-4">
        <h4 class="mb-3">{{ editingId ? 'Edit company' : 'New company' }}</h4>
        <div class="row">
          <div class="col-12 mb-3">
            <base-input label="Name">
              <el-select
                v-if="!editingId"
                v-model="form.name"
                class="w-100"
                filterable
                allow-create
                default-first-option
                placeholder="Select or type company name"
              >
                <el-option
                  v-for="option in companyNameOptions"
                  :key="option"
                  :label="option"
                  :value="option"
                />
              </el-select>
              <input
                v-else
                type="text"
                class="form-control"
                :value="form.name"
                disabled
              />
            </base-input>
            <small v-if="editingId" class="text-muted">
              Name can't be changed here — it must exactly match the "Company" value on user records
              for download limits to apply. Delete and recreate if a company is genuinely renamed
              (and update affected users' Company field to match).
            </small>
            <small v-else class="text-muted">
              Must exactly match (or will become) the "Company" value set on user records.
            </small>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6" v-for="type in matchTypes" :key="type.id">
            <h5 class="mb-3">{{ type.label }}</h5>
            <base-input
              label="Download limit (minutes)"
              v-model="form[type.id].downloadLimitMinutes"
              type="number"
              placeholder="Blank = unlimited"
              :min="0"
            />
            <base-input
              label="Max clip length (minutes)"
              v-model.number="form[type.id].maxClipMinutes"
              type="number"
              placeholder="Default 20"
              :min="1"
            />
          </div>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <base-button type="secondary" class="mr-2" @click="showFormModal = false">
            Cancel
          </base-button>
          <base-button type="primary" :disabled="!form.name || saving" @click="save">
            Save
          </base-button>
        </div>
      </div>
    </modal>

    <modal :show.sync="showBreakdownModal" size="lg" modal-classes="modal-scrollable">
      <div class="p-4">
        <h4 class="mb-3">
          Download usage
          <span v-if="breakdownCompany">— {{ breakdownCompany.name }}</span>
        </h4>
        <tabs value="Summary">
          <tab title="Summary">
            <p class="text-muted small mb-1" v-if="breakdownCompany">
              Domestic used: {{ formatMinutes(getUsed(breakdownCompany, 'domestic')) }} min
              <span v-if="getLimit(breakdownCompany, 'domestic') !== null">
                / {{ formatLimit(getLimit(breakdownCompany, 'domestic')) }} limit
              </span>
            </p>
            <p class="text-muted small" v-if="breakdownCompany">
              International used: {{ formatMinutes(getUsed(breakdownCompany, 'international')) }} min
              <span v-if="getLimit(breakdownCompany, 'international') !== null">
                / {{ formatLimit(getLimit(breakdownCompany, 'international')) }} limit
              </span>
            </p>
            <el-table
              v-if="breakdownRows.length"
              :data="breakdownRows"
              header-row-class-name="thead-light"
              max-height="400"
            >
              <el-table-column label="User" min-width="200">
                <template v-slot="{ row }">
                  <div>{{ row.name }}</div>
                  <div class="text-muted small" v-if="row.email">{{ row.email }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Domestic (min)" width="130" align="right">
                <template v-slot="{ row }">{{ formatMinutes(row.domestic) }}</template>
              </el-table-column>
              <el-table-column label="International (min)" width="150" align="right">
                <template v-slot="{ row }">{{ formatMinutes(row.international) }}</template>
              </el-table-column>
            </el-table>
            <p v-else class="text-muted mb-0">No downloads recorded yet for this company.</p>
          </tab>
          <tab title="History">
            <div class="row mb-3">
              <div class="col-md-4">
                <base-input label="User">
                  <el-select v-model="historyFilters.userId" clearable placeholder="All users" @change="loadHistory(true)">
                    <el-option
                      v-for="option in breakdownUserOptions"
                      :key="option.id"
                      :label="option.name"
                      :value="option.id"
                    />
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-3">
                <base-input label="Match type">
                  <el-select v-model="historyFilters.matchType" clearable placeholder="All" @change="loadHistory(true)">
                    <el-option
                      v-for="type in matchTypes"
                      :key="type.id"
                      :label="type.label"
                      :value="type.id"
                    />
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-2">
                <base-input label="From" type="date" v-model="historyFilters.dateFrom" @change="loadHistory(true)" />
              </div>
              <div class="col-md-2">
                <base-input label="To" type="date" v-model="historyFilters.dateTo" @change="loadHistory(true)" />
              </div>
            </div>
            <p v-if="historyError" class="text-danger small">{{ historyError }}</p>
            <p v-if="historyLoading" class="text-muted mb-0">
              <i class="fa fa-spinner fa-spin mr-1"></i>Loading…
            </p>
            <template v-else>
              <el-table
                v-if="historyRows.length"
                :data="historyRows"
                header-row-class-name="thead-light"
                max-height="400"
              >
                <el-table-column label="When" min-width="140">
                  <template slot-scope="scope">
                    <span>{{ formatDateTime(scope.row.downloadedAt) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="User" prop="userName" min-width="160">
                  <template slot-scope="scope">
                    <span>{{ scope.row.userName || scope.row.userId }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Type" prop="matchType" width="120">
                  <template slot-scope="scope">
                    <span>{{ scope.row.matchType === 'international' ? 'International' : 'Domestic' }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Duration (min)" prop="durationMinutes" width="130" align="right">
                  <template slot-scope="scope">
                    <span>{{ formatMinutes(scope.row.durationMinutes) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="File" prop="fileId" min-width="180" show-overflow-tooltip>
                  <template slot-scope="scope">
                    <span class="text-muted small">{{ scope.row.fileId }}</span>
                  </template>
                </el-table-column>
              </el-table>
              <p v-else class="text-muted mb-0">No download history found.</p>
            </template>
            <div class="d-flex justify-content-center mt-3" v-if="historyHasMore">
              <base-button size="sm" type="secondary" :disabled="historyLoadingMore" @click="loadHistory(false)">
                {{ historyLoadingMore ? 'Loading…' : 'Load more' }}
              </base-button>
            </div>
          </tab>
        </tabs>
        <div class="d-flex justify-content-end mt-3">
          <base-button type="secondary" @click="showBreakdownModal = false">Close</base-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  DEFAULT_MAX_CLIP_MINUTES,
  MATCH_TYPES,
  getLimitMinutes,
  getUsedMinutes,
  getMaxClipMinutes,
  formatMinutes,
  formatLimit,
} from "@/utils/companyLimits";
import { Firebase } from "@/firebase";
import Tabs from "@/components/Tabs/Tabs";
import Tab from "@/components/Tabs/Tab";
import moment from "moment";

function blankTypeForm() {
  return {
    downloadLimitMinutes: "",
    maxClipMinutes: DEFAULT_MAX_CLIP_MINUTES,
  };
}

const HISTORY_PAGE_SIZE = 25;

export default {
  name: 'CompaniesAdmin',
  components: {
    tabs: Tabs,
    tab: Tab,
  },
  data() {
    return {
      matchTypes: MATCH_TYPES,
      showFormModal: false,
      showBreakdownModal: false,
      editingId: null,
      saving: false,
      syncing: false,
      loading: true,
      breakdownCompanyId: null,
      historyRows: [],
      historyLoading: false,
      historyLoadingMore: false,
      historyHasMore: false,
      historyLastDoc: null,
      historyError: '',
      historyFilters: {
        userId: '',
        matchType: '',
        dateFrom: '',
        dateTo: '',
      },
      form: {
        name: '',
        domestic: blankTypeForm(),
        international: blankTypeForm(),
      },
    };
  },
  computed: {
    config() {
      return this.$store.getters['config/config'] || {};
    },
    companies() {
      return this.$store.getters['companies/array'] || [];
    },
    breakdownCompany() {
      if (!this.breakdownCompanyId) return null;
      return this.$store.getters['companies/byId'](this.breakdownCompanyId) || null;
    },
    companyNameOptions() {
      const fromConfig = Array.isArray(this.config.companies) ? this.config.companies : [];
      const fromDocs = this.companies.map((c) => c.name).filter(Boolean);
      const fromUsers = this.$store.getters['users/companies'] || [];
      return [...new Set([...fromConfig, ...fromDocs, ...fromUsers].filter(Boolean))].sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
    },
    breakdownRows() {
      if (!this.breakdownCompany || !this.breakdownCompany.usersUsage) return [];
      const usage = this.breakdownCompany.usersUsage;
      return Object.keys(usage)
        .map((userId) => {
          const user = this.$store.getters['users/byId'](userId) || {};
          const entry = usage[userId] || {};
          const isNested = entry && typeof entry === 'object';
          return {
            id: userId,
            name: user.name || user.email || userId,
            email: user.email || '',
            domestic: isNested ? Number(entry.domestic) || 0 : Number(entry) || 0,
            international: isNested ? Number(entry.international) || 0 : 0,
          };
        })
        .sort((a, b) => (b.domestic + b.international) - (a.domestic + a.international));
    },
    breakdownUserOptions() {
      if (!this.breakdownCompany || !this.breakdownCompany.usersUsage) return [];
      return Object.keys(this.breakdownCompany.usersUsage)
        .map((userId) => {
          const user = this.$store.getters['users/byId'](userId) || {};
          return { id: userId, name: user.name || user.email || userId };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.$store.dispatch('companies/fetchAndAdd'),
        this.$store.dispatch('users/fetchAndAdd', { limit: 0 }),
      ]);
      // Auto-import any company names not yet represented as a record. This is
      // a client-side fallback net; the calculateCompanies Cloud Function does
      // the same reconciliation server-side whenever a user's Company changes.
      if (this.companyNameOptions.length) {
        await this.syncFromConfig({ silent: true });
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    getLimit(company, type) {
      return getLimitMinutes(company, type);
    },
    getUsed(company, type) {
      return getUsedMinutes(company, type);
    },
    formatMinutes,
    formatLimit,
    async syncFromConfig(opts = {}) {
      if (this.syncing) return;
      this.syncing = true;
      try {
        const names = this.companyNameOptions;
        const existing = new Set(
          this.companies.map((c) => (c.name || '').toLowerCase()).filter(Boolean)
        );
        const toCreate = names.filter((n) => n && !existing.has(String(n).toLowerCase()));
        if (!toCreate.length) {
          if (!opts.silent) {
            this.$notify({
              type: 'info',
              message: 'All company names from users are already listed',
              timeout: 3000,
            });
          }
          return;
        }
        await Promise.all(
          toCreate.map((name) =>
            this.$store.dispatch('companies/insert', {
              name: String(name).trim(),
              downloadLimitMinutes: { domestic: null, international: null },
              downloadUsedMinutes: { domestic: 0, international: 0 },
              maxClipMinutes: { domestic: DEFAULT_MAX_CLIP_MINUTES, international: DEFAULT_MAX_CLIP_MINUTES },
              usersUsage: {},
            })
          )
        );
        if (!opts.silent) {
          this.$notify({
            type: 'success',
            message: `Added ${toCreate.length} compan${toCreate.length === 1 ? 'y' : 'ies'}`,
            timeout: 3000,
          });
        }
      } catch (e) {
        console.error(e);
        this.$notify({
          type: 'danger',
          message: e.message || 'Sync failed — check you have Users: Admin and that firestore.rules are deployed',
          timeout: 6000,
        });
      } finally {
        this.syncing = false;
      }
    },
    resetForm() {
      this.form = {
        name: '',
        domestic: blankTypeForm(),
        international: blankTypeForm(),
      };
    },
    openAdd() {
      this.editingId = null;
      this.resetForm();
      this.showFormModal = true;
    },
    openEdit(row) {
      this.editingId = row.id;
      const forType = (type) => {
        const limit = getLimitMinutes(row, type);
        return {
          downloadLimitMinutes: limit === null || limit === undefined ? '' : String(limit),
          maxClipMinutes: getMaxClipMinutes(row, type),
        };
      };
      this.form = {
        name: row.name || '',
        domestic: forType('domestic'),
        international: forType('international'),
      };
      this.showFormModal = true;
    },
    openBreakdown(row) {
      this.breakdownCompanyId = row.id;
      this.historyFilters = { userId: '', matchType: '', dateFrom: '', dateTo: '' };
      this.showBreakdownModal = true;
      this.refreshBreakdown(row.id);
    },
    async refreshBreakdown(companyId) {
      try {
        await this.$store.dispatch('companies/fetchById', companyId);
      } catch (e) {
        console.error(e);
      }
      await this.loadHistory(true);
      await this.reconcileUsageFromLogs(companyId);
    },
    async reconcileUsageFromLogs(companyId) {
      if (!companyId) return;
      try {
        const snap = await Firebase.firestore()
          .collection('downloadLog')
          .where('companyId', '==', companyId)
          .get();
        const downloadUsedMinutes = { domestic: 0, international: 0 };
        const usersUsage = {};
        snap.docs.forEach((doc) => {
          const data = doc.data() || {};
          const matchType = data.matchType === 'international' ? 'international' : 'domestic';
          const minutes = Number(data.durationMinutes) || 0;
          downloadUsedMinutes[matchType] += minutes;
          const userId = data.userId || 'unknown';
          if (!usersUsage[userId]) {
            usersUsage[userId] = { domestic: 0, international: 0 };
          }
          usersUsage[userId][matchType] += minutes;
        });
        await this.$store.dispatch('companies/patch', {
          id: companyId,
          downloadUsedMinutes,
          usersUsage,
        });
      } catch (e) {
        console.error(e);
      }
    },
    formatDateTime(value) {
      if (!value) return '';
      const d = value.toDate ? value.toDate() : new Date(value);
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
      return moment(d).format('DD/MM/YYYY HH:mm');
    },
    async loadHistory(reset) {
      if (!this.breakdownCompany) return;
      if (reset) {
        this.historyRows = [];
        this.historyLastDoc = null;
        this.historyHasMore = false;
      }
      const loadingKey = reset ? 'historyLoading' : 'historyLoadingMore';
      this[loadingKey] = true;
      this.historyError = '';
      try {
        let query = Firebase.firestore()
          .collection('downloadLog')
          .where('companyId', '==', this.breakdownCompany.id);

        if (this.historyFilters.userId) {
          query = query.where('userId', '==', this.historyFilters.userId);
        }
        if (this.historyFilters.matchType) {
          query = query.where('matchType', '==', this.historyFilters.matchType);
        }
        if (this.historyFilters.dateFrom) {
          query = query.where('downloadedAt', '>=', new Date(`${this.historyFilters.dateFrom}T00:00:00`));
        }
        if (this.historyFilters.dateTo) {
          query = query.where('downloadedAt', '<=', new Date(`${this.historyFilters.dateTo}T23:59:59.999`));
        }

        query = query.orderBy('downloadedAt', 'desc').limit(HISTORY_PAGE_SIZE);
        if (!reset && this.historyLastDoc) {
          query = query.startAfter(this.historyLastDoc);
        }

        const snap = await query.get();
        const newRows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        this.historyRows = reset ? newRows : [...this.historyRows, ...newRows];
        this.historyLastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : this.historyLastDoc;
        this.historyHasMore = snap.docs.length === HISTORY_PAGE_SIZE;
      } catch (e) {
        console.error(e);
        this.historyError =
          e.message ||
          'Failed to load download history — a Firestore index may need to be deployed (see firestore.indexes.json).';
      } finally {
        this[loadingKey] = false;
      }
    },
    parseLimitMinutes(raw) {
      if (raw === null || raw === undefined || String(raw).trim() === '') return null;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        throw new Error('Download limit must be a non-negative number or blank');
      }
      return n;
    },
    parseMaxClip(raw) {
      const n = Number(raw);
      return Number.isFinite(n) && n >= 1 ? n : DEFAULT_MAX_CLIP_MINUTES;
    },
    async save() {
      if (!this.form.name || this.saving) return;
      this.saving = true;
      try {
        const name = String(this.form.name).trim();
        if (!name) throw new Error('Name is required');

        const downloadLimitMinutes = {
          domestic: this.parseLimitMinutes(this.form.domestic.downloadLimitMinutes),
          international: this.parseLimitMinutes(this.form.international.downloadLimitMinutes),
        };
        const maxClipMinutes = {
          domestic: this.parseMaxClip(this.form.domestic.maxClipMinutes),
          international: this.parseMaxClip(this.form.international.maxClipMinutes),
        };

        const duplicate = this.companies.find(
          (c) =>
            c.id !== this.editingId &&
            (c.name || '').toLowerCase() === name.toLowerCase()
        );
        if (duplicate) {
          throw new Error(`A company named “${duplicate.name}” already exists`);
        }

        if (this.editingId) {
          // Name is intentionally not sent on patch — it's locked to the value
          // already in Firestore so it can't drift from users' Company field.
          await this.$store.dispatch('companies/patch', {
            id: this.editingId,
            downloadLimitMinutes,
            maxClipMinutes,
          });
        } else {
          await this.$store.dispatch('companies/insert', {
            name,
            downloadLimitMinutes,
            downloadUsedMinutes: { domestic: 0, international: 0 },
            maxClipMinutes,
            usersUsage: {},
          });
        }
        this.showFormModal = false;
        this.$notify({ type: 'success', message: 'Saved', timeout: 2000 });
      } catch (e) {
        console.error(e);
        this.$notify({
          type: 'danger',
          message: e.message || 'Save failed',
          timeout: 5000,
        });
      } finally {
        this.saving = false;
      }
    },
    resetUsage(row, type) {
      const label =
        type === 'all' ? 'both domestic and international usage' : `${type} usage`;
      if (
        !window.confirm(
          `Reset ${label} for “${row.name || row.id}”? This zeroes the meter but keeps the download log.`
        )
      ) {
        return;
      }
      const downloadUsedMinutes = {
        domestic: type === 'international' ? getUsedMinutes(row, 'domestic') : 0,
        international: type === 'domestic' ? getUsedMinutes(row, 'international') : 0,
      };
      const usersUsage = {};
      Object.keys(row.usersUsage || {}).forEach((userId) => {
        const entry = row.usersUsage[userId];
        const isNested = entry && typeof entry === 'object';
        const domestic = isNested ? Number(entry.domestic) || 0 : Number(entry) || 0;
        const international = isNested ? Number(entry.international) || 0 : 0;
        usersUsage[userId] = {
          domestic: type === 'international' ? domestic : 0,
          international: type === 'domestic' ? international : 0,
        };
      });

      this.$store
        .dispatch('companies/patch', {
          id: row.id,
          downloadUsedMinutes,
          usersUsage,
        })
        .then(() => {
          this.$notify({ type: 'success', message: 'Usage reset', timeout: 2000 });
        })
        .catch((e) => {
          console.error(e);
          this.$notify({
            type: 'danger',
            message: e.message || 'Reset failed',
            timeout: 5000,
          });
        });
    },
    confirmDelete(row) {
      if (!window.confirm(`Delete company “${row.name || row.id}”?`)) return;
      this.$store
        .dispatch('companies/delete', row.id)
        .then(() => {
          this.$notify({ type: 'success', message: 'Deleted', timeout: 2000 });
        })
        .catch((e) => {
          console.error(e);
          this.$notify({
            type: 'danger',
            message: e.message || 'Delete failed',
            timeout: 5000,
          });
        });
    },
  },
};
</script>
